import React, { useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Phone,
  Bed,
  Bath,
  Car,
  Maximize,
  Home,
  Share2,
  Heart,
  ChevronLeft,
  ChevronRight,
  X,
  Check,
  MessageCircle,
  Mail,
  User,
} from 'lucide-react';

// ✅ UI do próprio template Lovable (igual Properties.jsx)
import { Button } from '@/site/templates/TemplateCorretorLovable/ui/button';
import { Input } from '@/site/templates/TemplateCorretorLovable/ui/input';
import { Textarea } from '@/site/templates/TemplateCorretorLovable/ui/textarea';
import { Checkbox } from '@/site/templates/TemplateCorretorLovable/ui/checkbox';

/* ======================
   HELPERS
====================== */
const onlyDigits = (v = '') => String(v).replace(/\D/g, '');

const formatBRL = (value) => {
  const n = Number(value ?? 0);
  if (Number.isNaN(n)) return 'R$ 0';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

function getImageUrl(img) {
  if (!img) return null;
  if (typeof img === 'string') return img;
  if (typeof img === 'object') {
    return img.url || img.src || img.downloadURL || null;
  }
  return null;
}

function normalizeImages(p = {}) {
  const arr = Array.isArray(p.imagens)
    ? p.imagens
    : Array.isArray(p.images)
      ? p.images
      : [];

  const urls = arr.map((it) => getImageUrl(it)).filter(Boolean);

  const thumb = getImageUrl(p.thumbnail) || getImageUrl(p.capaUrl);
  if (thumb && !urls.includes(thumb)) urls.unshift(thumb);

  return urls;
}

function normalizeProperty(p = {}) {
  const id = p.id || p.objectID || p.uid || p.refCodigo || p.ref || '';

  const statusRaw = String(p.status || p.negocioTipo || p.finalidade || '')
    .toLowerCase()
    .trim();
  const status =
    statusRaw.includes('loc') || statusRaw.includes('alug')
      ? 'Locação'
      : 'Venda';

  const price = Number(p.preco ?? p.price ?? 0);
  const title = p.titulo || p.title || p.tipo || p.type || 'Imóvel';

  const neighborhood = p.bairro || p.neighborhood || '';
  const city = p.cidade || p.city || '';
  const state = p.estado || p.state || '';
  const location =
    p.location ||
    [neighborhood, city].filter(Boolean).join(', ') ||
    city ||
    '—';

  const beds = Number(p.dormitorios ?? p.beds ?? 0);
  const baths = Number(p.banheiros ?? p.baths ?? 0);
  const suites = Number(p.suites ?? p.suuites ?? 0);
  const parking = Number(p.vagas ?? p.parking ?? 0);

  const area =
    Number(p.areaConstruida ?? p.area ?? p.m2 ?? 0) ||
    Number(p.areaTerreno ?? 0);

  const builtArea = Number(p.areaConstruida ?? p.builtArea ?? 0);

  const type = p.tipo || p.type || 'Imóvel';

  const images = normalizeImages(p);
  const thumbnail = images.length ? images[0] : null;

  const description = p.descricao || p.description || '';
  const ref = p.refCodigo || p.ref || String(id || '—');

  const features = Array.isArray(p.features) ? p.features : [];
  const infrastructure = Array.isArray(p.infrastructure)
    ? p.infrastructure
    : [];

  return {
    id: String(id),
    ref,
    title,
    type,
    status,
    price,
    priceFormatted: formatBRL(price),
    neighborhood: neighborhood || '—',
    city: city || '—',
    state: state || '',
    location,
    beds,
    baths,
    suites,
    parking,
    area,
    builtArea,
    images,
    image: thumbnail,
    description,
    features,
    infrastructure,
    raw: p,
  };
}

/**
 * BasePath universal:
 * - se estiver em /u/:handle -> base = /u/:handle
 * - se estiver em /site/:slug -> base = /site/:slug
 */
function getBasePath({ handle, slug, pathname }) {
  const path = String(pathname || '').toLowerCase();

  if (path.startsWith('/u/')) {
    const h = String(handle || slug || '').trim();
    return h ? `/u/${h}` : '/u';
  }

  const s = String(slug || handle || '').trim();
  return s ? `/site/${s}` : '/site';
}

export default function PropertyDetail({
  siteConfig,
  properties = [],
  slug,
  handle,
}) {
  const { pathname } = useLocation();
  const { imovelId, id } = useParams();
  const propertyId = imovelId || id || '';

  const basePath = useMemo(
    () => getBasePath({ handle, slug, pathname }),
    [handle, slug, pathname]
  );

  const brandName = siteConfig?.displayName || siteConfig?.nome || 'Corretor';
  const whatsapp =
    siteConfig?.whatsapp ||
    siteConfig?.whatsappDisplay ||
    siteConfig?.telefone ||
    '';
  const waLink = whatsapp ? `https://wa.me/55${onlyDigits(whatsapp)}` : null;

  const all = useMemo(
    () => (Array.isArray(properties) ? properties : []).map(normalizeProperty),
    [properties]
  );

  const property = useMemo(() => {
    const pid = String(propertyId);
    return all.find((p) => String(p.id) === pid || String(p.ref) === pid);
  }, [all, propertyId]);

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [favorited, setFavorited] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    privacy: false,
  });

  const images = property?.images || [];
  const safeIndex = Math.min(lightboxIndex, Math.max(0, images.length - 1));

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };
  const closeLightbox = () => setLightboxOpen(false);

  const prevImage = () => {
    if (!images.length) return;
    setLightboxIndex((i) => (i === 0 ? images.length - 1 : i - 1));
  };

  const nextImage = () => {
    if (!images.length) return;
    setLightboxIndex((i) => (i === images.length - 1 ? 0 : i + 1));
  };

  const relatedProperties = useMemo(() => {
    if (!property) return [];
    const sameType = all
      .filter((p) => p.id !== property.id && p.type === property.type)
      .slice(0, 3);

    if (sameType.length < 3) {
      const extra = all
        .filter(
          (p) => p.id !== property.id && !sameType.some((x) => x.id === p.id)
        )
        .slice(0, 3 - sameType.length);
      sameType.push(...extra);
    }

    return sameType;
  }, [all, property]);

  if (!property) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-6">
        <div className="text-center max-w-md w-full rounded-2xl border border-border bg-card p-6">
          <h1 className="font-serif text-2xl mb-3">Imóvel não encontrado</h1>
          <p className="text-sm text-muted-foreground mb-6">
            Esse imóvel pode ter sido removido ou o link está incorreto.
          </p>
          <Link to={`${basePath}/imoveis`}>
            <Button variant="gold">Ver todos os imóveis</Button>
          </Link>
        </div>
      </div>
    );
  }

  const extraPhotos = images.length > 3 ? images.length - 3 : 0;

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="bg-background border-b border-border sticky top-0 z-50">
        <div className="container mx-auto px-4 lg:px-8">
          <nav className="flex items-center justify-between h-16 lg:h-20">
            <Link to={`${basePath}`} className="flex items-center gap-2">
              <span className="font-serif text-xl lg:text-2xl font-semibold text-foreground tracking-wide">
                {brandName}
              </span>
            </Link>

            <div className="flex items-center gap-2 lg:gap-4">
              <Link to={`${basePath}/imoveis`}>
                <Button variant="ghost" size="sm" className="gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  <span className="hidden sm:inline">Voltar</span>
                </Button>
              </Link>

              {waLink ? (
                <a href={waLink} target="_blank" rel="noreferrer">
                  <Button variant="gold" size="sm" className="gap-2">
                    <Phone className="w-4 h-4" />
                    <span className="hidden sm:inline">WhatsApp</span>
                  </Button>
                </a>
              ) : (
                <Button variant="gold" size="sm" className="gap-2" disabled>
                  <Phone className="w-4 h-4" />
                  <span className="hidden sm:inline">WhatsApp</span>
                </Button>
              )}
            </div>
          </nav>
        </div>
      </header>

      {/* Image Gallery */}
      <section className="container mx-auto px-4 lg:px-8 pt-6">
        {/* Desktop gallery */}
        <div className="hidden md:grid grid-cols-4 grid-rows-2 gap-2 rounded-2xl overflow-hidden h-[480px]">
          <div
            className="col-span-2 row-span-2 cursor-pointer relative group"
            onClick={() => images[0] && openLightbox(0)}
          >
            {images[0] ? (
              <img
                src={images[0]}
                alt={property.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            ) : (
              <div className="w-full h-full bg-muted flex items-center justify-center">
                <Home className="w-8 h-8 text-muted-foreground" />
              </div>
            )}
          </div>

          {images.slice(1, 3).map((img, i) => (
            <div
              key={img}
              className="cursor-pointer relative group"
              onClick={() => openLightbox(i + 1)}
            >
              <img
                src={img}
                alt={`${property.title} ${i + 2}`}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>
          ))}

          <div
            className="cursor-pointer relative group"
            onClick={() => images[3] && openLightbox(3)}
          >
            {images[3] ? (
              <>
                <img
                  src={images[3]}
                  alt={`${property.title} 4`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                {extraPhotos > 0 && (
                  <div className="absolute inset-0 bg-foreground/50 flex items-center justify-center transition-opacity group-hover:bg-foreground/60">
                    <span className="text-background font-semibold text-lg">
                      + {extraPhotos} fotos
                    </span>
                  </div>
                )}
              </>
            ) : (
              <div className="w-full h-full bg-muted flex items-center justify-center">
                <Home className="w-8 h-8 text-muted-foreground" />
              </div>
            )}
          </div>
        </div>

        {/* Mobile gallery slider */}
        <div className="md:hidden relative rounded-xl overflow-hidden aspect-[4/3]">
          {images[safeIndex] ? (
            <img
              src={images[safeIndex]}
              alt={property.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-muted flex items-center justify-center">
              <Home className="w-8 h-8 text-muted-foreground" />
            </div>
          )}

          <div className="absolute bottom-4 right-4 bg-foreground/70 text-background text-sm px-3 py-1 rounded-full">
            {Math.min(safeIndex + 1, images.length || 1)} / {images.length || 1}
          </div>

          <button
            onClick={prevImage}
            className="absolute left-3 top-1/2 -translate-y-1/2 bg-background/80 rounded-full p-2"
            aria-label="Anterior"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <button
            onClick={nextImage}
            className="absolute right-3 top-1/2 -translate-y-1/2 bg-background/80 rounded-full p-2"
            aria-label="Próxima"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          <button
            onClick={() => setLightboxOpen(true)}
            className="absolute bottom-4 left-4 bg-background/80 text-sm px-3 py-1 rounded-full"
          >
            Ver todas
          </button>
        </div>
      </section>

      {/* Content + Sidebar */}
      <section className="container mx-auto px-4 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content */}
          <div className="flex-1 space-y-8">
            {/* Title + Actions */}
            <div>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h1 className="font-serif text-2xl lg:text-4xl font-semibold text-foreground leading-tight">
                    {property.title} | {property.neighborhood} | {property.city}
                    {property.state ? `/${property.state}` : ''}
                  </h1>
                  <p className="text-muted-foreground mt-1">
                    Ref: {property.ref}
                  </p>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => setFavorited(!favorited)}
                    className="p-2 rounded-full border border-border hover:bg-muted transition-colors"
                    aria-label="Favoritar"
                  >
                    <Heart
                      className={`w-5 h-5 ${
                        favorited
                          ? 'fill-red-500 text-red-500'
                          : 'text-muted-foreground'
                      }`}
                    />
                  </button>

                  <button
                    onClick={() =>
                      navigator.clipboard?.writeText(window.location.href)
                    }
                    className="p-2 rounded-full border border-border hover:bg-muted transition-colors"
                    aria-label="Compartilhar"
                  >
                    <Share2 className="w-5 h-5 text-muted-foreground" />
                  </button>
                </div>
              </div>

              {/* Price */}
              <div className="mt-4">
                <p className="font-serif text-3xl lg:text-4xl font-bold text-gold">
                  {property.priceFormatted}
                </p>
                {property.status === 'Locação' && (
                  <span className="text-muted-foreground text-sm">/mês</span>
                )}
              </div>
            </div>

            {/* Key Features Row */}
            <div className="flex flex-wrap gap-6 py-6 border-y border-border">
              <div className="flex items-center gap-2">
                <Bed className="w-5 h-5 text-gold" />
                <div>
                  <p className="font-semibold text-foreground">
                    {property.beds || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">Dormitórios</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Bed className="w-5 h-5 text-gold" />
                <div>
                  <p className="font-semibold text-foreground">
                    {property.suites || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">Suítes</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Bath className="w-5 h-5 text-gold" />
                <div>
                  <p className="font-semibold text-foreground">
                    {property.baths || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">Banheiros</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Car className="w-5 h-5 text-gold" />
                <div>
                  <p className="font-semibold text-foreground">
                    {property.parking || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">Vagas</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Maximize className="w-5 h-5 text-gold" />
                <div>
                  <p className="font-semibold text-foreground">
                    {property.area ? `${property.area}m²` : '—'}
                  </p>
                  <p className="text-xs text-muted-foreground">Área total</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Home className="w-5 h-5 text-gold" />
                <div>
                  <p className="font-semibold text-foreground">
                    {property.builtArea ? `${property.builtArea}m²` : '—'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Área construída
                  </p>
                </div>
              </div>
            </div>

            {/* Features */}
            {property.features?.length > 0 && (
              <div>
                <h2 className="font-serif text-2xl font-semibold text-foreground mb-4">
                  Características do Imóvel
                </h2>
                <div className="grid grid-cols-2 gap-3">
                  {property.features.map((f) => (
                    <div
                      key={f}
                      className="flex items-center gap-2 text-sm text-muted-foreground"
                    >
                      <Check className="w-4 h-4 text-gold shrink-0" />
                      {f}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Infrastructure */}
            {property.infrastructure?.length > 0 && (
              <div>
                <h2 className="font-serif text-2xl font-semibold text-foreground mb-4">
                  Infraestrutura
                </h2>
                <div className="grid grid-cols-2 gap-3">
                  {property.infrastructure.map((f) => (
                    <div
                      key={f}
                      className="flex items-center gap-2 text-sm text-muted-foreground"
                    >
                      <Check className="w-4 h-4 text-gold shrink-0" />
                      {f}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Description */}
            {!!property.description && (
              <div>
                <h2 className="font-serif text-2xl font-semibold text-foreground mb-4">
                  Descrição
                </h2>
                <div className="prose prose-sm max-w-none text-muted-foreground leading-relaxed whitespace-pre-line">
                  {property.description}
                </div>
              </div>
            )}
          </div>

          {/* Contact Sidebar */}
          <aside className="w-full lg:w-[380px] shrink-0">
            <div className="lg:sticky lg:top-28 space-y-4">
              <div className="bg-card border border-border rounded-2xl p-6 shadow-card">
                <h3 className="font-serif text-xl font-semibold text-foreground mb-1">
                  Gostou deste imóvel?
                </h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Preencha o formulário e entraremos em contato.
                </p>

                <div className="space-y-4">
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Seu nome"
                      className="pl-10"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                    />
                  </div>

                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Telefone"
                      className="pl-10"
                      value={formData.phone}
                      onChange={(e) =>
                        setFormData({ ...formData, phone: e.target.value })
                      }
                    />
                  </div>

                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="E-mail"
                      className="pl-10"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                    />
                  </div>

                  <Textarea
                    placeholder="Mensagem (opcional)"
                    rows={3}
                    value={formData.message}
                    onChange={(e) =>
                      setFormData({ ...formData, message: e.target.value })
                    }
                  />

                  <div className="flex items-start gap-2">
                    <Checkbox
                      id="privacy"
                      checked={formData.privacy}
                      onCheckedChange={(v) =>
                        setFormData({ ...formData, privacy: v === true })
                      }
                    />
                    <label
                      htmlFor="privacy"
                      className="text-xs text-muted-foreground leading-tight cursor-pointer"
                    >
                      Li e aceito a política de privacidade e autorizo o
                      contato.
                    </label>
                  </div>

                  <Button
                    variant="gold"
                    size="lg"
                    className="w-full gap-2"
                    disabled={!formData.privacy}
                    onClick={() => {
                      if (!formData.privacy) return;
                      const msg = `Olá! Tenho interesse no imóvel ${property.ref} - ${property.title}.
Nome: ${formData.name}
Telefone: ${formData.phone}
Email: ${formData.email}
Mensagem: ${formData.message || '-'}`;
                      if (waLink) {
                        window.open(
                          `${waLink}?text=${encodeURIComponent(msg)}`,
                          '_blank'
                        );
                      }
                    }}
                  >
                    <MessageCircle className="w-4 h-4" />
                    Solicitar Visita
                  </Button>
                </div>
              </div>

              {waLink ? (
                <Button
                  variant="goldOutline"
                  size="lg"
                  className="w-full gap-2"
                  onClick={() =>
                    window.open(
                      `${waLink}?text=${encodeURIComponent(
                        `Olá! Tenho interesse no imóvel ${property.ref} - ${property.title}`
                      )}`,
                      '_blank'
                    )
                  }
                >
                  <Phone className="w-4 h-4" />
                  Falar no WhatsApp
                </Button>
              ) : (
                <Button
                  variant="goldOutline"
                  size="lg"
                  className="w-full gap-2"
                  disabled
                >
                  <Phone className="w-4 h-4" />
                  WhatsApp indisponível
                </Button>
              )}
            </div>
          </aside>
        </div>
      </section>

      {/* Mobile contact bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-background border-t border-border p-3 flex gap-2 z-40">
        <Button
          variant="gold"
          size="lg"
          className="flex-1 gap-2"
          onClick={() => {
            if (!waLink) return;
            window.open(
              `${waLink}?text=${encodeURIComponent(
                `Olá! Tenho interesse no imóvel ${property.ref} - ${property.title}`
              )}`,
              '_blank'
            );
          }}
          disabled={!waLink}
        >
          <Phone className="w-4 h-4" />
          WhatsApp
        </Button>

        <Button
          variant="goldOutline"
          size="lg"
          className="flex-1 gap-2"
          onClick={() => setLightboxOpen(true)}
          disabled={!images.length}
        >
          <MessageCircle className="w-4 h-4" />
          Fotos
        </Button>
      </div>

      {/* Related Properties */}
      {relatedProperties.length > 0 && (
        <section className="container mx-auto px-4 lg:px-8 py-12 lg:py-16 border-t border-border">
          <h2 className="font-serif text-3xl font-semibold text-foreground mb-8">
            Imóveis Relacionados
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {relatedProperties.map((rp) => (
              <Link
                key={rp.id}
                to={`${basePath}/imovel/${rp.id}`}
                className="group"
              >
                <article className="bg-card rounded-2xl overflow-hidden border border-border hover:border-gold/30 transition-all duration-300 shadow-card hover:shadow-medium">
                  <div className="aspect-[4/3] overflow-hidden relative">
                    {rp.image ? (
                      <img
                        src={rp.image}
                        alt={rp.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full bg-muted flex items-center justify-center">
                        <span className="text-muted-foreground text-sm">
                          Sem imagem
                        </span>
                      </div>
                    )}

                    <div className="absolute top-3 left-3 flex gap-2">
                      <span className="px-3 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-full">
                        {rp.type}
                      </span>
                      <span className="px-3 py-1 bg-gold text-primary-foreground text-xs font-medium rounded-full">
                        {rp.status}
                      </span>
                    </div>
                  </div>

                  <div className="p-5 space-y-3">
                    <p className="font-serif text-xl font-semibold text-gold">
                      {rp.priceFormatted}
                    </p>
                    <h3 className="font-serif text-lg font-semibold text-foreground">
                      {rp.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {rp.location}
                    </p>

                    <div className="flex items-center gap-4 pt-3 border-t border-border text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Bed className="w-4 h-4" />
                        {rp.beds || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <Bath className="w-4 h-4" />
                        {rp.baths || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <Maximize className="w-4 h-4" />
                        {rp.area ? `${rp.area}m²` : '—'}
                      </span>
                    </div>
                  </div>
                </article>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="bg-foreground py-8">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <p className="text-muted/80 text-sm">
            © {new Date().getFullYear()} {brandName}. Todos os direitos
            reservados.
          </p>
        </div>
      </footer>

      {/* Floating WhatsApp */}
      {waLink && (
        <button
          onClick={() =>
            window.open(
              `${waLink}?text=${encodeURIComponent(
                `Olá! Tenho interesse no imóvel ${property.ref} - ${property.title}`
              )}`,
              '_blank'
            )
          }
          className="hidden lg:flex fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white px-5 py-3 rounded-full items-center gap-2 shadow-lg transition-colors"
          aria-label="WhatsApp"
        >
          <Phone className="w-5 h-5" />
          Fale Conosco
        </button>
      )}

      {/* Lightbox */}
      {lightboxOpen && images.length > 0 && (
        <div className="fixed inset-0 z-[100] bg-foreground/95 flex items-center justify-center animate-fade-in">
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-background hover:text-gold p-2"
            aria-label="Fechar"
          >
            <X className="w-8 h-8" />
          </button>

          <button
            onClick={prevImage}
            className="absolute left-4 text-background hover:text-gold p-2"
            aria-label="Anterior"
          >
            <ChevronLeft className="w-10 h-10" />
          </button>

          <button
            onClick={nextImage}
            className="absolute right-4 text-background hover:text-gold p-2"
            aria-label="Próxima"
          >
            <ChevronRight className="w-10 h-10" />
          </button>

          <img
            src={images[safeIndex]}
            alt={`${property.title} ${safeIndex + 1}`}
            className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg"
          />

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-background text-sm">
            {Math.min(safeIndex + 1, images.length)} / {images.length}
          </div>
        </div>
      )}
    </div>
  );
}
